import { createHash } from "node:crypto";

/**
 * T4 HANDOFF (Agent Brains 10 and 10.1). The baton between two agents.
 *
 * An agent that hands work to another writes `handoff.offered` and enqueues the
 * receiver's task. The receiver writes `handoff.accepted` the moment it claims
 * the task, before any work (`withHandoffAck`, packages/db/src/handoffs.ts). A
 * baton nobody picked up inside its ack SLA is a dropped baton, and a dropped
 * baton is loud (CLAUDE.md section 0): silent degradation is the defined enemy.
 *
 * Pure. The queries and the sweep live in packages/db.
 */

/**
 * The 16 contract kinds a baton can carry, copied from the design's
 * `ContractKind` union (DESIGN-spine T3). T3 owns `roleCharters.ts` and the
 * canonical type; this list is T4's own so it builds without that file, and
 * the integrator replaces it with the import then.
 */
export const HANDOFF_CONTRACTS = [
  "lead_card",
  "lead_score",
  "reply_draft",
  "booking_intent",
  "call_prep_brief",
  "reactivation_request",
  "call_list",
  "review_ask_draft",
  "review_response_draft",
  "review_synthesis",
  "content_suggestion",
  "insight",
  "digest",
  "proposal",
  "campaign_plan",
  "outcome_label",
] as const;

export type HandoffContract = (typeof HANDOFF_CONTRACTS)[number];

const CONTRACT_SET: ReadonlySet<string> = new Set(HANDOFF_CONTRACTS);

export function isHandoffContract(value: string): value is HandoffContract {
  return CONTRACT_SET.has(value);
}

/**
 * How long a receiver has to claim an offered baton before it is dropped.
 * 5 minutes for every contract. AWAITING RATIFICATION: the number is a
 * conservative reading (the design says "default 5 min ... T3 sla-timers"),
 * and an SLA timer is T3, human-only, so Kabir sets the real figure per
 * contract. Nothing here lets an agent tune it.
 */
export const DEFAULT_HANDOFF_ACK_SLA_MS = 5 * 60 * 1000;

export const HANDOFF_ACK_SLA_MS: Readonly<Record<HandoffContract, number>> = Object.freeze(
  Object.fromEntries(HANDOFF_CONTRACTS.map((c) => [c, DEFAULT_HANDOFF_ACK_SLA_MS])) as Record<
    HandoffContract,
    number
  >,
);

/**
 * Deterministic id for one baton: the same contract, subject and causing event
 * always give the same id, so a replay of the bus offers once (the events
 * unique index on `handoff.offered:{handoffId}` is the backstop).
 *
 * The parts are joined with a NUL, which none of them can contain in practice,
 * so ("ab", "c") and ("a", "bc") never collide.
 */
export function handoffId(contract: HandoffContract, subjectId: string, causeEventId: string): string {
  if (!isHandoffContract(contract)) throw new Error(`handoffId: unknown contract ${String(contract)}`);
  if (subjectId.trim() === "") throw new Error("handoffId: subjectId is empty");
  if (causeEventId.trim() === "") throw new Error("handoffId: causeEventId is empty");
  const digest = createHash("sha256").update(`${contract}\u0000${subjectId}\u0000${causeEventId}`).digest("hex");
  return `ho_${digest.slice(0, 24)}`;
}

/** What `batonDropped` needs to know of an offer. `dueAckAt` is a Date or the ISO string a payload carries. */
export interface BatonOffer {
  readonly handoffId: string;
  readonly dueAckAt: Date | string;
}

/** An answer to a baton: a `handoff.accepted` or `handoff.declined` row, reduced to its id. */
export interface BatonAck {
  readonly handoffId: string;
}

function dueMs(offer: BatonOffer): number {
  const ms = offer.dueAckAt instanceof Date ? offer.dueAckAt.getTime() : Date.parse(offer.dueAckAt);
  if (Number.isNaN(ms)) {
    throw new Error(`batonDropped: dueAckAt of ${offer.handoffId} is not a readable time`);
  }
  return ms;
}

/**
 * Dropped: strictly past the due time with no answer for this baton. Any ack or
 * decline answers it, a late one included (the `late` flag on the accepted row
 * keeps that fact). An unreadable due time throws; it never reads as "fine".
 */
export function batonDropped(offer: BatonOffer, acks: readonly BatonAck[], now: Date): boolean {
  if (now.getTime() <= dueMs(offer)) return false;
  return !acks.some((a) => a.handoffId === offer.handoffId);
}

/** Milliseconds past the due time, zero when not yet due. */
export function batonOverdueMs(offer: BatonOffer, now: Date): number {
  return Math.max(0, now.getTime() - dueMs(offer));
}
